import React, { useEffect, useCallback } from 'react';
import { useIntl } from 'react-intl';

import { useShallowEqualSelector } from '../../../../../hooks/redux';
import { scrollToTop } from '../../../../../hooks/useScrollToTop';

import { ReduxState } from '../../../../../redux/reducers';
import { getInputs } from '../../../../../redux/selectors/merchants/products/purchases/inputs';
import {
  SchemeType,
  MerchantProduct,
  SettingConfirmation,
} from '../../../../../interfaces/merchants/products/purchases';
import {
  inputPurchasingConfirmation,
  RestartError,
  ReturnTargetStepError,
} from '../../../../../redux/actions/merchants/products/purchases/inputs';

import PurchaseTicketBookItemList from '../../../../../molecules/merchant/products/purchases/PurchaseTicketBookItemList';
import PurchaseSubscriptionItemList from '../../../../../molecules/merchant/products/purchases/PurchaseSubscriptionItemList';
import PurchaseConfirmAlert from './components/PurchaseConfirmAlert';
import { FirstCharge } from '../../../../../interfaces/merchants/products/firstCharge';
import { useAppDispatch } from '../../../../../redux';
import { RSButton } from 'src/reservation-page/RSButton';

interface Props {
  schemeType: SchemeType;
  product: MerchantProduct;
  firstCharge: FirstCharge | null;
  settings: SettingConfirmation;
  postable: boolean;
  onSubmit: () => void;
  onRestart: () => void;
  onReturnTargetStep: () => void;
  publicId: string;
}

const InputConfirmation: React.FC<Props> = (props) => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    scrollToTop();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const useSelector = () =>
    useShallowEqualSelector((state: ReduxState) => ({
      inputs: getInputs(state.merchantsProductsPurchasesInputs),
    }));
  const { inputs } = useSelector();

  const handleSubmit = useCallback(async () => {
    const { schemeType, onSubmit, onRestart, onReturnTargetStep } = props;

    try {
      await dispatch(
        inputPurchasingConfirmation(schemeType, inputs.productId!, inputs),
      );
      onSubmit();
    } catch (e) {
      if (e instanceof ReturnTargetStepError) {
        onReturnTargetStep();
      }
      if (e instanceof RestartError) {
        onRestart();
      }
    }
  }, [dispatch, props, inputs]);

  const { product, settings, postable, schemeType } = props;
  const intl = useIntl();
  const labelType =
    schemeType === SchemeType.Subscription ? 'subscription' : 'ticketBook';

  return (
    <>
      <PurchaseConfirmAlert
        labelType={labelType}
        termsUrl="/user_terms"
        lawUrl={`/${props.publicId}/legal_notice`}
      />
      {schemeType === SchemeType.TicketBook && (
        <PurchaseTicketBookItemList
          productImage={
            product.image ? product.image.thumb && product.image.thumb.url : ''
          }
          productName={product.name}
          merchantId={props.publicId}
          ticketCount={product.ticketCount}
          price={product.price}
          expiryDate={settings.expiryDate}
        />
      )}
      {schemeType === SchemeType.Subscription && (
        <PurchaseSubscriptionItemList
          productImage={
            product.image ? product.image.thumb && product.image.thumb.url : ''
          }
          productName={product.name}
          merchantId={props.publicId}
          waitFirstAnchor={product.waitFirstAnchor}
          firstCharge={props.firstCharge}
          price={product.price}
          availableMonthsPeriod={product.availableMonthsPeriod}
        />
      )}
      <div className="px-[18px] py-5">
        <RSButton
          variant="basic"
          isBlock
          isLoading={!postable}
          disabled={!postable}
          value={intl.formatMessage({
            id: `purchases.inputConfirmation.submitButton.${labelType}`,
          })}
          onClick={handleSubmit}
        >
          {intl.formatMessage({
            id: `purchases.inputConfirmation.submitButton.${labelType}`,
          })}
        </RSButton>
      </div>
    </>
  );
};

export default InputConfirmation;
